import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api } from './api';

export interface SiteAdAccount {
  id: string;
  name: string;
  loginCustomerId: string;
}

/**
 * Google Ads accounts to analyse for the ACTIVE site. The advertising pages are
 * project-scoped routes (/projects/:id/ads/*), so the site is the URL `:id` — the
 * single source of truth (no cross-site leak). Connection is workspace-wide; the
 * analysed accounts are the ones linked to THIS site in Settings.
 */
export function useSiteAdAccounts(): {
  projectId: string | null;
  connected: boolean;
  accounts: SiteAdAccount[];
  loading: boolean;
} {
  const { id } = useParams();
  const projectId = id ?? null;

  const statusQ = useQuery({ queryKey: ['gads-status'], queryFn: () => api.googleAdsStatus() });
  const connected = !!statusQ.data?.connected;

  const linkedQ = useQuery({
    queryKey: ['gads-site-accounts', projectId],
    queryFn: () => api.googleAdsSiteAccounts(projectId as string),
    enabled: connected && !!projectId,
  });
  const accounts = (linkedQ.data?.accounts ?? []).map((a) => ({
    id: a.customerId,
    name: a.name,
    loginCustomerId: a.loginCustomerId,
  }));

  return {
    projectId,
    connected,
    accounts,
    loading: statusQ.isLoading || (connected && !!projectId && linkedQ.isLoading),
  };
}
